import { useState, useRef, useEffect, useMemo } from "react";
import { C } from "../../../lib/tokens.js";
import { t } from "../../../lib/data.js";
import { useUser } from "../../../lib/UserCacheContext.jsx";
import { useToast } from "../../../lib/ToastContext.jsx";
import { relTime, dateTimeFull, isAgentRole } from "../../../lib/utils.js";
import { Button, IconButton } from "../../../components/ui/Button.jsx";
import { Spinner } from "../../../components/ui/Misc.jsx";
import { StatusChip, Chip } from "../../../components/ui/Chips.jsx";
import { Avatar } from "../../../components/ui/Avatar.jsx";
import { useLookups } from "../../../lib/LookupsContext.jsx";
import { useAttachments, downloadAttachment, deleteAttachment, deleteAttachmentErrorMessage, getActivityText } from "./shared.jsx";
import { useAutoGrowTextarea } from "../../../lib/useAutoGrowTextarea.js";
import { ImageLightbox } from "../../../components/ui/ImageLightbox.jsx";
import { AttachmentItem } from "./AttachmentItem.jsx";
import { Linkify } from "../../../components/ui/Linkify.jsx";

export function RequestDescriptionBlock({ request, reporterUser, isMe, onRefresh, me, role, onUpdate }) {
  const { addToast } = useToast();
  const [lightboxMedia, setLightboxMedia] = useState(null);
  const { attachments, loading } = useAttachments(request.attachments);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(request.description);
  const [saving, setSaving] = useState(false);
  const draftRef = useAutoGrowTextarea(draft);
  const isAgent = isAgentRole(role, me);

  // Resets the inline editor when navigating between requests, since this component
  // instance persists across route param changes rather than remounting.
  useEffect(() => {
    setEditing(false);
    setDraft(request.description);
  }, [request.id, request.description]);

  if (!request) return null;

  async function handleDelete(a) {
    try {
      await deleteAttachment(a.id);
      await onRefresh?.(request.id);
    } catch (err) {
      addToast(deleteAttachmentErrorMessage(err));
    }
  }

  function cancelEdit() {
    setDraft(request.description);
    setEditing(false);
  }

  async function saveEdit() {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === request.description) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      await onUpdate?.(request.id, { description: trimmed });
      await onRefresh?.(request.id);
      setEditing(false);
    } catch (err) {
      addToast("Could not update the description.");
    } finally {
      setSaving(false);
    }
  }

  const items = attachments.map((a) => ({
    ...a,
    onDownload: (onProgress) => downloadAttachment(a, onProgress),
    onDelete: me?.canDeleteAttachments ? () => handleDelete(a) : undefined,
  }));

  return (
    <>
      <div style={{ background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 6, padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <Avatar user={reporterUser ?? request.reporterId} size={28} />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: C.fg }}>
              {reporterUser?.name ?? request.reporterId}{" "}
              {isMe ? <span style={{ color: C.silver, fontWeight: 400 }}>(you)</span> : null}
            </span>
            <span style={{ fontSize: 11, color: C.iron }}>
              opened this request · <span title={dateTimeFull(request.createdAt)}>{relTime(request.createdAt)}</span>
            </span>
          </div>
          <span style={{ flex: 1 }} />
          {isAgent && !editing ? (
            <IconButton icon="edit" size={20} title="Edit description" onClick={() => setEditing(true)} />
          ) : null}
        </div>

        {editing ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <textarea
              ref={draftRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") cancelEdit();
              }}
              autoFocus
              rows={3}
              disabled={saving}
              style={{
                fontFamily: "Roboto", fontSize: 13, lineHeight: 1.6, color: C.fg, background: C.platinum,
                border: `1px solid ${C.liquid}`, borderRadius: 4, padding: "8px 10px", outline: "none",
                resize: "none", overflow: "hidden", boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button kind="outline" size="sm" onClick={cancelEdit} disabled={saving}>Cancel</Button>
              <Button kind="primary" size="sm" onClick={saveEdit} disabled={saving}>Save</Button>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: C.fg, lineHeight: 1.6, whiteSpace: "pre-wrap", overflowWrap: "break-word" }}>
            <Linkify text={request.description} />
          </div>
        )}

        {loading && (
          <div style={{ fontSize: 11, color: C.iron, marginTop: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <Spinner size={14} />
            Loading attachments…
          </div>
        )}

        {!loading && items.length > 0 && (
          <div
            style={{
              display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 10, marginTop: 12, paddingTop: 12, borderTop: `1px dashed ${C.mercury}`,
            }}
          >
            {items.map((a, i) => (
              <AttachmentItem key={i} a={a} onOpenLightbox={(url, kind) => setLightboxMedia({ url, kind })} />
            ))}
          </div>
        )}
      </div>

      {lightboxMedia && <ImageLightbox url={lightboxMedia.url} kind={lightboxMedia.kind} onClose={() => setLightboxMedia(null)} />}
    </>
  );
}

export function ActivityFeed({ request, reply, setReply, attachments, setAttachments, onSend, role, isMe, me, onRefresh, onUpdate }) {
  const textareaRef = useAutoGrowTextarea(reply);
  const fileInputRef = useRef();
  const reporterUser = useUser(request.reporterId);
  const currentUser = me?.id ? me : { id: "me", name: "Me", initials: "ME", role: me?.role ?? "customer" };
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [confidentialType, setConfidentialType] = useState("A");

  // Backend returns activity newest-first already; oldest-first just reverses it locally.
  // The description block (the request being opened) always stays pinned at the top,
  // regardless of direction — only the activity items below it reorder.
  const [oldestFirst, setOldestFirst] = useState(false);

  const isAgent = isAgentRole(role, me);

  const blobUrls = useMemo(
    () => attachments.map((f) => (f.type.startsWith("image/") ? URL.createObjectURL(f) : null)),
    [attachments],
  );
  useEffect(() => () => blobUrls.forEach((u) => u && URL.revokeObjectURL(u)), [blobUrls]);

  function addFiles(files) {
    if (!files || files.length === 0) return;
    setAttachments((a) => [...a, ...Array.from(files)]);
  }

  function handleDragOver(e) {
    const hasFiles = Array.from(e.dataTransfer?.types ?? []).includes("Files");
    if (!hasFiles) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setIsDraggingFiles(true);
  }

  function handleSendClick() {
    if (typeof onSend === "function") {
      onSend(confidentialType);
    }
  }

  function handleDragLeave(e) {
    const related = e.relatedTarget;
    if (related && e.currentTarget.contains(related)) return;
    setIsDraggingFiles(false);
  }

  function handleDrop(e) {
    const hasFiles = Array.from(e.dataTransfer?.types ?? []).includes("Files");
    if (!hasFiles) return;
    e.preventDefault();
    setIsDraggingFiles(false);
    addFiles(e.dataTransfer?.files);
  }

  const orderedActivity = oldestFirst ? [...request.activity].reverse() : request.activity;
  const description = (
    <RequestDescriptionBlock request={request} reporterUser={reporterUser} isMe={isMe} onRefresh={onRefresh} me={me} role={role} onUpdate={onUpdate} />
  );

  return (
    <div key={request.id} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {description}

      <div style={{ height: 2, background: C.mercury }} />

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={() => setOldestFirst((v) => !v)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px",
            border: "none", background: "none", cursor: "pointer",
            color: C.iron, fontSize: 12, fontFamily: "Roboto",
          }}
        >
          <span className="material-icons" style={{ fontSize: 15 }}>
            {oldestFirst ? "arrow_downward" : "arrow_upward"}
          </span>
          {oldestFirst ? "Oldest first" : "Newest first"}
        </button>
      </div>

      {orderedActivity.map((ev, i) => (
        <ActivityItem key={ev.id ?? i} ev={ev} requestId={request.id} onRefresh={onRefresh} me={me} />
      ))}

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          background: isDraggingFiles ? C.rowActive : C.tin,
          border: `1px solid ${isDraggingFiles ? C.liquid : C.mercury}`,
          borderRadius: 6, padding: 14, display: "flex", flexDirection: "column", gap: 10,
        }}
      >
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <Avatar user={currentUser} size={28} />
          <textarea
            ref={textareaRef}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onPaste={(e) => {
              try {
                const items = e.clipboardData?.items;
                if (!items) return;
                const files = [];
                for (let i = 0; i < items.length; i++) {
                  const it = items[i];
                  if (it && it.kind === "file") {
                    const f = it.getAsFile();
                    if (f) files.push(f);
                  }
                }
                if (files.length) setAttachments((a) => [...a, ...files]);
              } catch (err) {
                // ignore
              }
            }}
            placeholder={t("req.detail.add_comment")}
            rows={1}
            style={{
              flex: 1, fontFamily: "Roboto", fontSize: 13, padding: "8px 10px",
              border: `1px solid ${C.mercury}`, borderRadius: 4, outline: "none",
              resize: "none", overflowY: "auto", overflowX: "hidden", minHeight: 64, maxHeight: 300, lineHeight: 1.5, color: C.fg, background: C.tin,
            }}
          />
        </div>
        {isDraggingFiles ? (
          <div style={{ fontSize: 12, color: C.liquid, fontWeight: 500, padding: "2px 2px 0" }}>
            Drop files to attach them.
          </div>
        ) : null}
        {attachments.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {attachments.map((f, i) => {
              const img = f.type.startsWith("image/");
              const blobUrl = blobUrls[i];
              return (
                <div
                  key={i}
                  onClick={img ? () => setLightboxUrl(blobUrl) : undefined}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 8px",
                    background: C.platinum, border: `1px solid ${C.mercury}`, borderRadius: 4,
                    fontSize: 12, color: C.fg, cursor: img ? "pointer" : "default",
                  }}
                >
                  {img ? (
                    <img src={blobUrl} alt="" style={{ width: 24, height: 24, borderRadius: 3, objectFit: "cover", flex: "none" }} />
                  ) : (
                    <span className="material-icons" style={{ fontSize: 13, color: C.iron }}>description</span>
                  )}
                  <span>{f.name}</span>
                  <IconButton
                    icon="close"
                    title="Remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAttachments((a) => a.filter((_, j) => j !== i));
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              setAttachments((a) => [...a, ...Array.from(e.target.files)]);
              e.target.value = "";
            }}
          />
          <IconButton icon="attach_file" title="Attach files" onClick={() => fileInputRef.current?.click()} />
          <span style={{ flex: 1 }} />

          {isAgent && (
            <select
              value={confidentialType}
              onChange={(e) => setConfidentialType(e.target.value)}
              disabled={reply.trim() === ""}
              style={{
                fontFamily: "Roboto", fontSize: 12, padding: "5px 8px", borderRadius: 4,
                border: `1px solid ${C.mercury}`, background: C.tin, color: C.fg, outline: "none",
                marginRight: 4, opacity: reply.trim() === "" ? 0.5 : 1, maxWidth: 140,
                cursor: reply.trim() === "" ? "not-allowed" : "pointer",
              }}
            >
              <option value="A">Public Information</option>
              <option value="C">Partner Confidential</option>
              {role === "agent" && <option value="I">Internal</option>}
            </select>
          )}

          <Button kind="primary" icon="send" size="sm" onClick={handleSendClick} disabled={!reply.trim()}>
            {t("req.detail.send")}
          </Button>
        </div>
      </div>
      {lightboxUrl !== null && <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
    </div>
  );
}

function ActivityItem({ ev, requestId, onRefresh, me }) {
  const { statuses } = useLookups();
  const { addToast } = useToast();
  const user = useUser(ev.by);
  const author = ev.actor ?? user;
  const authorName = author?.name ?? ev.by ?? "Unknown";

  const [lightboxMedia, setLightboxMedia] = useState(null);
  const rawAttachments = ev.kind === "comment" ? ev.attachments : null;
  const { attachments, loading } = useAttachments(rawAttachments);

  async function handleDelete(a) {
    try {
      await deleteAttachment(a.id);
      await onRefresh?.(requestId);
    } catch (err) {
      addToast(deleteAttachmentErrorMessage(err));
    }
  }

  const items = attachments.map((a) => ({
    ...a,
    onDownload: (onProgress) => downloadAttachment(a, onProgress),
    onDelete: me?.canDeleteAttachments ? () => handleDelete(a) : undefined,
  }));

  if (ev.kind === "comment") {
    const text = getActivityText(ev);

    return (
      <>
        <div style={{ background: ev.internal ? `${C.brass}14` : C.tin, border: `1px solid ${ev.internal ? `${C.brass}66` : C.mercury}`, borderRadius: 6, padding: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
            <Avatar user={author ?? ev.by} size={26} />
            <span style={{ fontSize: 13, fontWeight: 500, color: C.fg }}>{authorName}</span>
            {author?.role === "agent" && (
              <Chip bg={C.rowActive} color={C.deepsea} size="sm" icon="support_agent">
                ActFact Support
              </Chip>
            )}
            {ev.internal && (
              <Chip bg={`${C.brass}22`} color={C.brass} size="sm" icon="lock">
                Internal
              </Chip>
            )}
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: C.iron }} title={dateTimeFull(ev.at)}>{relTime(ev.at)}</span>
          </div>

          <div style={{ fontSize: 13, color: C.fg, lineHeight: 1.6, whiteSpace: "pre-wrap", overflowWrap: "break-word" }}>
            <Linkify text={text} />
          </div>

          {loading && (
            <div style={{ fontSize: 11, color: C.iron, marginTop: 10, display: "flex", alignItems: "center", gap: 6 }}>
              <Spinner size={14} />
              Loading attachments…
            </div>
          )}

          {!loading && items.length > 0 && (
            <div
              style={{
                display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                gap: 10, marginTop: 12, paddingTop: 12, borderTop: `1px dashed ${C.mercury}`,
              }}
            >
              {items.map((a, i) => (
                <AttachmentItem key={i} a={a} onOpenLightbox={(url, kind) => setLightboxMedia({ url, kind })} />
              ))}
            </div>
          )}
        </div>

        {lightboxMedia && <ImageLightbox url={lightboxMedia.url} kind={lightboxMedia.kind} onClose={() => setLightboxMedia(null)} />}
      </>
    );
  }

  if (ev.kind === "status") {
    const from = statuses.find((s) => s.id === ev.from);
    const to = statuses.find((s) => s.id === ev.to);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 14px", fontSize: 12, color: C.iron, flexWrap: "wrap" }}>
        <div style={{ width: 26, display: "flex", justifyContent: "center" }}>
          <span className="material-icons" style={{ fontSize: 16, color: C.iron }}>swap_horiz</span>
        </div>
        <Avatar user={author ?? ev.by} size={20} />
        <span>{authorName}</span>
        {from ? (
          <>
            <span>moved status from</span>
            <StatusChip id={from.id} size="sm" filled readOnly />
            <span>to</span>
          </>
        ) : (
          <span>moved status to</span>
        )}
        <StatusChip id={to?.id} size="sm" filled readOnly />
        <span style={{ flex: 1 }} />
        <span title={dateTimeFull(ev.at)}>{relTime(ev.at)}</span>
      </div>
    );
  }

  if (ev.kind === "field" && ev.field === "assignee_id") {
    return <AssigneeActivityRow ev={ev} author={author} authorName={authorName} />;
  }

  return null;
}

function AssigneePersonChip({ userId, label }) {
  if (!userId) {
    return (
      <Chip bg={C.platinum} color={C.silver} icon="person_off" size="sm">
        Unassigned
      </Chip>
    );
  }
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px 2px 4px",
      borderRadius: 999, background: C.rowActive, color: C.deepsea, fontSize: 11, fontWeight: 500,
    }}>
      <Avatar user={userId} size={16} />
      {label}
    </span>
  );
}

function AssigneeActivityRow({ ev, author, authorName }) {
  const isUnassignedValue = (v) => !v || v === "0" || String(v).toLowerCase() === "null";
  const fromId = isUnassignedValue(ev.from) ? null : ev.from;
  const toId = isUnassignedValue(ev.to) ? null : ev.to;
  const fromUser = useUser(fromId);
  const toUser = useUser(toId);
  const fromLabel = fromUser?.name ?? fromId;
  const toLabel = toUser?.name ?? toId;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 14px", fontSize: 12, color: C.iron, flexWrap: "wrap" }}>
      <div style={{ width: 26, display: "flex", justifyContent: "center" }}>
        <span className="material-icons" style={{ fontSize: 16, color: C.iron }}>assignment_ind</span>
      </div>
      <Avatar user={author ?? ev.by} size={20} />
      <span>{authorName}</span>
      {fromId ? (
        <>
          <span>reassigned this from</span>
          <AssigneePersonChip userId={fromId} label={fromLabel} />
          <span>to</span>
          <AssigneePersonChip userId={toId} label={toLabel} />
        </>
      ) : (
        <>
          <span>assigned this to</span>
          <AssigneePersonChip userId={toId} label={toLabel} />
        </>
      )}
      <span style={{ flex: 1 }} />
      <span title={dateTimeFull(ev.at)}>{relTime(ev.at)}</span>
    </div>
  );
}